import {
    faClockRotateLeft,
    faCopy,
    faExternalLink,
    faEye,
    faEyeSlash,
    faLink,
    faNoteSticky,
    faPenToSquare,
    faTrash,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Dispatch, Key, ReactElement, SetStateAction} from 'react';
import {generatePath} from 'react-router';

import {appRoute} from '../../../../../../../app-route';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {useModal} from '../../../../../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../../../../../provider/snackbar/snackbar-hook';
import {CustomReviewError, fetchDeleteReview} from '../../../../../../../service/reviews/custom-reviews';
import {ReviewPresenceBlockedActionEnum} from '../../../../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {ReviewPresenceBlockedMenuLabel} from '../../../../review-realtime-presence/review-presence-blocked-menu-label/review-presence-blocked-menu-label';
import {constructCommentString, convertCatalogCommentAttributes} from '../review-helper';

type ReviewMenuOptionsType = {
    catalogId?: number;
    text: string;
    reviewId: number;
    isShowExternalOptions: boolean;
    setIsOpenNoteModal: Dispatch<SetStateAction<boolean>>;
    setIsOpenHistoryModal: Dispatch<SetStateAction<boolean>>;
    setIsOpenResponsibleUserModal: Dispatch<SetStateAction<boolean>>;
    ableToChangeResponsibleUser: boolean;
    isPresenceLocked: boolean;
    onSubmitFinish: () => void;
    isShowOpenNewTabButton?: boolean;
    isReviewDeleted: boolean;
    isHidden: boolean;
    onSetHidden: (nextHidden: boolean) => Promise<void> | void;
};

type ReviewMenuHookType = {
    key: Key;
    disabled?: boolean;
    label: ReactElement | string;
    icon: ReactElement;
    onClick?: () => void | Promise<void>;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useReviewMenu(options: ReviewMenuOptionsType): Array<ReviewMenuHookType> {
    const {
        catalogId,
        setIsOpenHistoryModal,
        ableToChangeResponsibleUser,
        isPresenceLocked,
        setIsOpenNoteModal,
        reviewId,
        text,
        isShowExternalOptions,
        setIsOpenResponsibleUserModal,
        onSubmitFinish,
        isShowOpenNewTabButton = true,
        isReviewDeleted,
        isHidden,
        onSetHidden,
    } = options;

    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const {modal} = useModal();

    async function handleVisibilityMenuClick() {
        if (isReviewDeleted) {
            return;
        }

        if (isHidden) {
            await onSetHidden(false);

            return;
        }

        modal.confirm({
            title: <Locale stringKey="REVIEWS__REVIEW_HIDE__MODAL_TITLE" />,
            content: <Locale stringKey="REVIEWS__REVIEW_HIDE__MODAL_CONTENT" />,
            onOk: () => onSetHidden(true),
            okText: getLocalizedString('REVIEWS__REVIEW_HIDE__MODAL_OK'),
            cancelText: getLocalizedString('POPUP__BUTTON__CANCEL'),
        });
    }

    function handleDeleteClick() {
        modal.confirm({
            title: <Locale stringKey="CUSTOM_REIVEW__DELETE_MODAL_TITLE" />,
            content: <Locale stringKey="CUSTOM_REIVEW__DELETE_MODAL_CONTENT" />,
            onOk: (closeModal) =>
                fetchDeleteReview(reviewId)
                    .then(() => {
                        closeModal();
                        onSubmitFinish();

                        snackbar.success({
                            message: <Locale stringKey="CUSTOM_REIVEW__DELETE__SUCCESS__TITLE" />,
                            description: <Locale stringKey="CUSTOM_REIVEW__DELETE__SUCCESS__DESCRIPTION" />,
                        });
                    })
                    .catch((error: unknown) => {
                        if (error instanceof CustomReviewError) {
                            snackbar.error((error as Error).message);
                        } else {
                            snackbar.error({
                                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                                description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
                            });
                        }
                    }),
            okButtonProps: {danger: true, type: 'primary'},
            okText: getLocalizedString('POPUP__BUTTON__CONFIRM'),
            cancelText: getLocalizedString('POPUP__BUTTON__CANCEL'),
        });
    }

    async function onCopy() {
        const commentString = constructCommentString(convertCatalogCommentAttributes(text, catalogId));

        if (typeof navigator?.clipboard?.writeText !== 'function') {
            snackbar.error(<Locale stringKey="REVIEWS__REVIEW_COPY__BROWSER_ERROR" />);
            return;
        }

        try {
            await navigator.clipboard.writeText(commentString);
            snackbar.success(<Locale stringKey="REVIEWS__REVIEW_COPY__SUCCESS" />);
        } catch (error: unknown) {
            snackbar.error({
                description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
            });

            throw error;
        }
    }

    async function onCopyLink() {
        try {
            const url = location.origin + generatePath(appRoute.reviewsManagementReviewItem.path, {id: reviewId});

            await navigator.clipboard.writeText(url);
            snackbar.success(<Locale stringKey="REVIEWS__REVIEW_URL_COPY__SUCCESS" />);
        } catch (error: unknown) {
            snackbar.error({
                description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
            });

            throw error;
        }
    }

    const additionalMenuItem = isShowExternalOptions
        ? [
              {
                  key: 'edit',
                  icon: <FontAwesomeIcon icon={faPenToSquare} />,
                  label: getLocalizedString('REVIEWS__REVIEW_EDIT__HINT'),
                  onClick: () =>
                      window.open(generatePath(appRoute.reviewsManagementEditReview.path, {reviewId}), '_blank'),
              },
              {
                  key: 'delete',
                  icon: <FontAwesomeIcon icon={faTrash} />,
                  label: getLocalizedString('REVIEWS__REVIEW_DELETE__HINT'),
                  onClick: handleDeleteClick,
              },
          ]
        : [];

    const visibilityMenuItem = !isReviewDeleted
        ? [
              {
                  key: 'toggle-visibility',
                  icon: <FontAwesomeIcon icon={isHidden ? faEye : faEyeSlash} />,
                  label: getLocalizedString(isHidden ? 'REVIEWS__REVIEW_UNHIDE__HINT' : 'REVIEWS__REVIEW_HIDE__HINT'),
                  onClick: handleVisibilityMenuClick,
              },
          ]
        : [];

    const responsibleUserItem = ableToChangeResponsibleUser
        ? [
              {
                  key: 'responsible',
                  disabled: isPresenceLocked,
                  icon: <FontAwesomeIcon icon={faUser} />,
                  label: (
                      <ReviewPresenceBlockedMenuLabel
                          blockedAction={ReviewPresenceBlockedActionEnum.ResponsibleUser}
                          isBlocked={isPresenceLocked}
                      >
                          {getLocalizedString('REVIEW__DROPDOWN_MENU__APPOINT_RESPONSIBLE')}
                      </ReviewPresenceBlockedMenuLabel>
                  ),
                  onClick: () => {
                      if (!isPresenceLocked) {
                          setIsOpenResponsibleUserModal(true);
                      }
                  },
              },
          ]
        : [];

    const openLinkButton = isShowOpenNewTabButton
        ? [
              {
                  key: 'open-link',
                  icon: <FontAwesomeIcon icon={faExternalLink} />,
                  label: (
                      <a
                          href={
                              location.origin + generatePath(appRoute.reviewsManagementReviewItem.path, {id: reviewId})
                          }
                          rel="noreferrer"
                          target="_blank"
                      >
                          {getLocalizedString('REVIEW__DROPDOWN_MENU__OPEN_LINK')}
                      </a>
                  ),
              },
          ]
        : [];

    return [
        ...openLinkButton,
        {
            key: 'copy',
            icon: <FontAwesomeIcon icon={faCopy} />,
            label: getLocalizedString('REVIEWS__REVIEW_COPY__HINT'),
            onClick: onCopy,
        },
        {
            key: 'link',
            icon: <FontAwesomeIcon icon={faLink} />,
            label: getLocalizedString('REVIEWS__REVIEW_URL_COPY__HINT'),
            onClick: onCopyLink,
        },
        {
            key: 'note',
            icon: <FontAwesomeIcon icon={faNoteSticky} />,
            label: getLocalizedString('REVIEW__DROPDOWN_MENU__ADD_NOTE'),
            onClick: () => setIsOpenNoteModal(true),
        },
        ...responsibleUserItem,
        {
            key: 'history',
            icon: <FontAwesomeIcon icon={faClockRotateLeft} />,
            label: getLocalizedString('REVIEW_ITEM_PAGE__ACTIVITY__LOGS'),
            onClick: () => setIsOpenHistoryModal(true),
        },
        ...visibilityMenuItem,
        ...additionalMenuItem,
    ];
}
